import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { X, Trophy, RefreshCw, Activity, Zap } from "lucide-react";
import { initials, timeAgo } from "@/lib/lifeos";
import { avatarForId } from "@/lib/avatars";

export default function ScoreboardPanel({ onClose, onFocusAgent }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.getScoreboard().then(setData).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const maxHour = data ? Math.max(1, ...data.hourly) : 1;
  const maxTasks = data && data.board.length ? Math.max(1, ...data.board.map((b) => b.tasks_completed)) : 1;

  const heatColor = (v) => {
    const t = v / maxHour;
    if (v === 0) return "rgba(255,255,255,0.04)";
    const alpha = 0.2 + t * 0.8;
    return `rgba(0,240,255,${alpha})`;
  };

  return (
    <div className="max-w-5xl mx-auto pt-8 px-4 pb-24">
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-display uppercase tracking-widest text-cyan-300 text-lg flex items-center gap-2"><Trophy size={18} /> Agent Scoreboard</h2>
        <div className="flex items-center gap-2">
          <button data-testid="scoreboard-refresh" onClick={load} className="w-8 h-8 grid place-items-center rounded-lg bg-white/5 hover:bg-white/10 text-zinc-400"><RefreshCw size={15} className={loading ? "animate-spin" : ""} /></button>
          <button data-testid="scoreboard-close-button" onClick={onClose} className="w-8 h-8 grid place-items-center rounded-lg bg-white/5 hover:bg-white/10 text-zinc-400"><X size={16} /></button>
        </div>
      </div>

      {data && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
            <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">Total Tasks</div>
              <div className="font-display font-black text-2xl text-cyan-300 mt-1">{data.total_tasks}</div>
            </div>
            <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">Active Agents</div>
              <div className="font-display font-black text-2xl text-emerald-300 mt-1">{data.total_agents}</div>
            </div>
            <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">On Autopilot</div>
              <div className="font-display font-black text-2xl text-amber-300 mt-1">{data.board.filter((b) => b.autopilot).length}</div>
            </div>
          </div>

          {/* heatmap */}
          <div className="rounded-xl border border-white/10 bg-[#080b11]/70 p-4 mb-6">
            <div className="flex items-center gap-2 text-[11px] font-tactical font-bold uppercase tracking-wider text-zinc-300 mb-3"><Activity size={14} className="text-cyan-400" /> Activity Heatmap · last 24h</div>
            <div data-testid="activity-heatmap" className="flex gap-1 items-end">
              {data.hourly.map((v, i) => (
                <div key={i} className="flex-1 group relative">
                  <div className="w-full rounded-sm transition-all" style={{ height: 46, background: heatColor(v) }} title={`${v} events`} />
                </div>
              ))}
            </div>
            <div className="flex justify-between text-[9px] font-mono text-zinc-600 mt-1"><span>24h ago</span><span>now</span></div>
          </div>

          {/* ranking */}
          <div className="space-y-2">
            {data.board.map((b, i) => (
              <div
                key={b.agent_id}
                data-testid="scoreboard-row"
                onClick={() => onFocusAgent && onFocusAgent(b.agent_id)}
                className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.02] hover:bg-white/[0.05] p-3 cursor-pointer transition-colors"
              >
                <div className="w-7 text-center font-display font-black text-lg" style={{ color: i === 0 ? "#ffb454" : i === 1 ? "#cbd5e1" : i === 2 ? "#f59e0b" : "#64748b" }}>{i + 1}</div>
                <span className="rounded-full overflow-hidden shrink-0" style={{ width: 40, height: 40, border: `2px solid ${b.accent}`, background: "#0b0e14" }}><img src={avatarForId(b.agent_id)} alt="" className="w-full h-full object-cover object-top" /></span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-tactical font-bold text-zinc-100 truncate">{b.name}</span>
                    {b.autopilot && <Zap size={12} className="text-amber-400" title="Autopilot on" />}
                  </div>
                  <div className="text-[11px] font-mono text-zinc-500 truncate">{b.role} · {b.department}</div>
                  <div className="mt-1.5 h-1.5 rounded-full bg-white/10 overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${(b.tasks_completed / maxTasks) * 100}%`, background: b.accent }} />
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-display font-black text-xl" style={{ color: b.accent }}>{b.tasks_completed}</div>
                  <div className="text-[9px] uppercase tracking-wider text-zinc-600 font-mono">tasks</div>
                  <div className="text-[9px] font-mono text-zinc-600 mt-0.5">{b.last_active ? timeAgo(b.last_active) + " ago" : "idle"}</div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
